import { type McpDefinition, type McpSecret } from "@afar-research/contracts";

/** Validate before persistence or provider delivery; errors never quote user input. */
export function mcpDefinitionIssue(definition: McpDefinition): string | undefined {
  if (definition.name === "afar-research-app")
    return "The name afar-research-app is reserved for AFAR's tools.";
  if (!/^[A-Za-z][A-Za-z0-9_-]{0,63}$/.test(definition.name))
    return "Use a server name starting with a letter, followed by letters, digits, underscores or dashes (up to 64 characters).";
  if (definition.transport === "http") {
    let url: URL;
    try {
      url = new URL(definition.url);
    } catch {
      return "Enter an absolute HTTP or HTTPS URL.";
    }
    let decoded = definition.url;
    try {
      decoded = decodeURIComponent(decoded);
    } catch {
      /* Invalid escapes are rejected below by the provider. */
    }
    if (url.username || url.password || /\$|\{\{|%[A-Za-z_][A-Za-z0-9_]*%/.test(decoded))
      return "URL credentials and secret substitutions are not supported. Use a bearer token or header instead.";
    if (url.protocol !== "https:" && url.protocol !== "http:") return "Use an HTTP or HTTPS URL.";
  } else if (!definition.command.trim()) return "Enter the command the provider should launch.";
  const entries = definition.transport === "stdio" ? definition.environment : definition.headers;
  const names = new Set<string>();
  for (const entry of entries) {
    const name = definition.transport === "http" ? entry.name.toLowerCase() : entry.name;
    if (names.has(name)) return "Each environment variable or header name must be unique.";
    names.add(name);
    if (
      !(
        definition.transport === "stdio"
          ? /^[A-Za-z_][A-Za-z0-9_]*$/
          : /^[!#$%&'*+.^_`|~0-9A-Za-z-]+$/
      ).test(entry.name)
    )
      return "Enter a valid environment variable or HTTP header name.";
    if (entry.name.startsWith("AFAR_RESEARCH_"))
      return "AFAR_RESEARCH_ environment variables are reserved.";
  }
  return undefined;
}

export function mapMcpSecrets(
  definition: McpDefinition,
  map: (secret: McpSecret, slot: string) => McpSecret,
): McpDefinition {
  return definition.transport === "stdio"
    ? {
        ...definition,
        environment: definition.environment.map((entry) => ({
          ...entry,
          secret: map(entry.secret, `env:${entry.name}`),
        })),
      }
    : {
        ...definition,
        ...(definition.bearerToken ? { bearerToken: map(definition.bearerToken, "bearer") } : {}),
        headers: definition.headers.map((entry) => ({
          ...entry,
          secret: map(entry.secret, `header:${entry.name}`),
        })),
      };
}

/**
 * Validate a user-authored set: every definition on its own, plus the rules
 * that only a set can break.
 *
 * Nothing is reserved for the Computer Use Service any more. AFAR derives no
 * MCP server for it, so there is no name a user-authored server could collide
 * with, and refusing one would only forbid a user from configuring their own
 * computer-use server in their own Environment.
 */
export function mcpDefinitionsIssue(definitions: ReadonlyArray<McpDefinition>): string | undefined {
  const names = new Set<string>();
  for (const definition of definitions) {
    const issue = mcpDefinitionIssue(definition);
    if (issue) return issue;
    if (names.has(definition.name))
      return "Each MCP server name must be unique within the Environment.";
    names.add(definition.name);
  }
  return undefined;
}
